import { useId, useState, type ReactNode } from "react";

import { X, Search, ShieldCheck } from "lucide-react";

import type { TaxonomyPayload } from "../../lib/api";

import { PostingReview } from "../../components/PostingReview";

import { SmartSelect } from "../../components/SmartInputs";
import { Req } from "../../components/ui/kit";
import { PSIC_SECTIONS, PSIC_DIVISIONS, psicCodeForName } from "../../data/psic";

import "../../components/CompanyManagementMobile.css";
import { ROLE_PAGE_SIZE, Form, StepProps, PendingTokens } from "./model";
import { SecHead, OptionalSection, BusinessOption, Chips } from "./FieldControls";
export function StepRoles({ tax, form, set, issue }: StepProps) {
  const fieldId = useId();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [limit, setLimit] = useState(ROLE_PAGE_SIZE);
  const selected = new Set(form.businessRoles);
  const toggle = (r: string) => {
    const nx = new Set(selected);
    nx.has(r) ? nx.delete(r) : nx.add(r);
    set("businessRoles", [...nx]);
  };
  const ql = q.trim().toLowerCase();
  // [business.taxonomy-and-readiness](../../../../docs/product/businesses.md#business-taxonomy-and-readiness): filters only affect browsing; selections remain visible and retain their saved values.
  const matches = tax.businessRoleCategories.filter(cat => !category || cat.name === category)
    .flatMap(cat => cat.roles.filter(role => !ql || role.toLowerCase().includes(ql) || cat.name.toLowerCase().includes(ql)));
  const clearFilters = () => { setQ(""); setCategory(""); setLimit(ROLE_PAGE_SIZE); };
  return (
    <div className="space-y-4">
      <SecHead title="What does your business do?" sub="Select at least one role. You can choose more than one." />
      {issue?.field === "businessRoles" && <p id={`${fieldId}-error`} role="alert" className="text-xs text-brand-red mt-3">{issue.message}</p>}
      <div>
        <p className="label">Selected roles <span className="font-normal text-mut">({form.businessRoles.length})</span></p>
        {form.businessRoles.length ? <div className="flex flex-wrap gap-1.5">
          {form.businessRoles.map(role => <span key={role} className="inline-flex items-center gap-1 rounded-lg border border-line bg-navy-50 px-3 py-2 text-xs font-semibold text-navy">
            {role}<button type="button" aria-label={`Remove business role ${role}`} onClick={() => toggle(role)} className="grid h-5 w-5 place-items-center rounded text-brand-red hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy"><X className="h-3.5 w-3.5" aria-hidden="true" /></button>
          </span>)}
        </div> : <p className="text-xs text-mut">No roles selected yet.</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <div>
          <label htmlFor={`${fieldId}-search`} className="label">Search roles</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-mut" aria-hidden="true" />
            <input id={`${fieldId}-search`} aria-label="Search business roles" data-company-field="businessRoles" className="field pl-9" value={q} aria-invalid={issue?.field === "businessRoles"} aria-describedby={issue?.field === "businessRoles" ? `${fieldId}-error` : undefined} onChange={event => { setQ(event.target.value); setLimit(ROLE_PAGE_SIZE); }} placeholder="e.g. supplier, manufacturing, consultant" />
          </div>
        </div>
        <div>
          <label htmlFor={`${fieldId}-category`} className="label">Browse by category</label>
          <select id={`${fieldId}-category`} className="field" value={category} onChange={event => { setCategory(event.target.value); setLimit(ROLE_PAGE_SIZE); }}>
            <option value="">All categories</option>
            {tax.businessRoleCategories.map(cat => <option key={cat.name} value={cat.name}>{cat.name}</option>)}
          </select>
        </div>
      </div>
      <div role="region" aria-label="Available business roles" className="rounded-lg border border-line p-3">
        {matches.length ? <div className="flex max-h-52 flex-wrap gap-1.5 overflow-y-auto">
          {matches.slice(0, limit).map(role => <BusinessOption key={role} label={role} selected={selected.has(role)} onClick={() => toggle(role)} />)}
        </div> : <p role="status" className="text-sm text-mut">No roles match. Try a different search or clear the filters.</p>}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-mut">
          <span>Showing {Math.min(limit, matches.length)} of {matches.length} roles</span>
          <div className="flex gap-3">
            {(q || category) && <button type="button" onClick={clearFilters} className="rounded py-1 font-semibold text-brand-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy">Clear role filters</button>}
            {limit < matches.length && <button type="button" onClick={() => setLimit(value => value + ROLE_PAGE_SIZE)} className="rounded py-1 font-semibold text-brand-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy">Show more roles</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function StepIndustry({ tax, form, set, issue, pendingTokens, setPendingTokens, showExtras, part }: StepProps & { pendingTokens: PendingTokens; setPendingTokens: (value: PendingTokens) => void; showExtras: boolean; part?: "industry" | "offerings" }) {
  const fieldId = useId();
  return <div className="space-y-5">
    {part !== "offerings" && <><SecHead title="What's your main industry?" sub="Choose a suggestion or enter your own line of business." />
    <div>
      <label htmlFor={`${fieldId}-industry`} className="label">Primary industry <Req /></label>
      <SmartSelect id={`${fieldId}-industry`} data-company-field="primaryIndustry" value={form.primaryIndustry} maxLength={220} options={[...new Set([...PSIC_DIVISIONS, ...PSIC_SECTIONS].map(industry => industry.name))]} combobox preserveValueOnFocus invalid={issue?.field === "primaryIndustry"} describedBy={issue?.field === "primaryIndustry" ? `${fieldId}-error` : undefined}
        onChange={value => {
          set("primaryIndustry", value);
          set("psicCode", psicCodeForName(value) || "");
        }} placeholder="e.g. Construction, Retail, Information technology" />
      {issue?.field === "primaryIndustry" && <p id={`${fieldId}-error`} role="alert" className="text-xs text-brand-red mt-2">{issue.message}</p>}
    </div></>}
    {showExtras && part !== "industry" && <OptionalSection title="Additional industries and offerings">
      <div><p className="label">Secondary industries (optional, up to 5)</p><Chips label="Secondary industries" draftValue={pendingTokens.secondaryIndustries} onDraftChange={value => setPendingTokens({ ...pendingTokens, secondaryIndustries: value })} value={form.secondaryIndustries} max={5} onChange={value => set("secondaryIndustries", value)} placeholder="Type an industry and press Enter" /></div>
      <div><p className="label">Products &amp; services (optional)</p><Chips label="Products & services" draftValue={pendingTokens.productsServices} onDraftChange={value => setPendingTokens({ ...pendingTokens, productsServices: value })} value={form.productsServices} max={40} onChange={value => set("productsServices", value)} placeholder="Add a product or service and press Enter" /></div>
      <div><p className="label">Customer / project markets (optional)</p><Chips label="Customer / project markets" draftValue={pendingTokens.customerMarkets} onDraftChange={value => setPendingTokens({ ...pendingTokens, customerMarkets: value })} value={form.customerMarkets} max={40} onChange={value => set("customerMarkets", value)} placeholder="e.g. Government, Private developers, Retail…" /></div>
      <div><p className="label">Service coverage (optional)</p><div className="flex flex-wrap gap-1.5">{tax.serviceCoverage.map(value => <BusinessOption key={value} label={value} selected={form.serviceCoverage === value} onClick={() => set("serviceCoverage", form.serviceCoverage === value ? "" : value)} />)}</div></div>
    </OptionalSection>}
  </div>;
}

export function StepReview({ tax, form, selectedType, subtypeOther, logoName, coverName, editors, disabled, showExtras }: {
  tax: TaxonomyPayload;
  form: Form;
  selectedType: TaxonomyPayload["organizationTypes"][number] | null;
  subtypeOther: string;
  logoName: string;
  coverName: string;
  editors: Record<"basics" | "location" | "profile" | "roles" | "industry", ReactNode>;
  disabled: boolean;
  showExtras: boolean;
}) {
  const rows: [string, string, keyof typeof editors][] = [
    ["Name", form.name, "basics"],
    ["Cover photo", coverName, "basics"],
    ["Tagline", form.tagline, "basics"],
    ["Location", form.city, "location"],
    ["Represents", form.profileRepresents, "location"],
    ["Company logo", logoName, "basics"],
    ["Organization type", selectedType?.name || "", "profile"],
    ["Subtype", /other|specify/i.test(form.subtype) && subtypeOther.trim() ? subtypeOther.trim() : form.subtype, "profile"],
    ["Ownership", form.ownershipClassifications.join(", "), "profile"],
    ["Enterprise class", form.enterpriseClassifications.join(", "), "profile"],
    ["Corporate relationship", form.corporateRelationship, "profile"],
    ["Operating status", form.operatingStatus, "profile"],
    ["Business roles", form.businessRoles.join(", "), "roles"],
    ["Primary industry", form.primaryIndustry, "industry"],
    ["Secondary industries", form.secondaryIndustries.join(", "), "industry"],
    ["Products & services", form.productsServices.join(", "), "industry"],
    ["Customer markets", form.customerMarkets.join(", "), "industry"],
    ["Service coverage", form.serviceCoverage, "industry"],
  ];
  const registrationRows = new Set(["Name", "Location", "Company logo", "Organization type", "Subtype", "Business roles", "Primary industry"]);
  const visibleRows = rows.filter(([label, value]) => showExtras || (registrationRows.has(label) && (label !== "Subtype" || Boolean(value))));
  return (
    <div className="card p-5 sm:p-6">
      <SecHead title="Review your business profile" sub="Check your details before submitting for verification." />
      <PostingReview disabled={disabled} fields={visibleRows.map(([label, value, group]) => ({ id: label.toLowerCase().replace(/[^a-z]+/g, "-"), label, value, editor: editors[group] }))} />
      <div className="mt-4 rounded-lg bg-navy/5 p-4 text-sm text-mut flex gap-2">
        <ShieldCheck className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
        <span>{tax.verifiedBadgeRule} Your business becomes public only after approval. Save a draft if you are not ready to submit proof.</span>
      </div>
    </div>
  );
}
